import React from 'react';
import { ArrowDown, UserRound, Briefcase, Mic, GraduationCap, Users, BookOpen, Award, Compass, MessagesSquare, Building2 } from 'lucide-react';
import { Reveal } from '../Reveal';
import { Cta } from '../Cta';
import { useScore } from '../ScoreContext';
const readerPath = ['Find what applies', 'Understand where they are', 'Take a useful next move', 'Indicate what they need'];
const knownContext = ['What matters', 'Where they are stuck', 'Actions they have taken', 'What kind of help may fit'];
const businessPaths = [{
  label: 'Coaching',
  Icon: UserRound
}, {
  label: 'Consulting',
  Icon: Briefcase
}, {
  label: 'Speaking',
  Icon: Mic
}, {
  label: 'Training',
  Icon: GraduationCap
}, {
  label: 'Workshops',
  Icon: Users
}, {
  label: 'Courses',
  Icon: BookOpen
}, {
  label: 'Certification',
  Icon: Award
}, {
  label: 'Advisory',
  Icon: Compass
}, {
  label: 'Communities',
  Icon: MessagesSquare
}, {
  label: 'Team implementation',
  Icon: Building2
}];
const enablementGroups = [{
  label: 'Get in the room',
  items: ['Podcast and interview preparation', 'Keynote and speaking angles']
}, {
  label: 'Create the next experience',
  items: ['Workshop outlines', 'Training material', 'CTA paths']
}, {
  label: 'Continue the conversation',
  items: ['Follow-up assets', 'Offer language', 'Sales conversations', 'Content angles']
}];
function Down() {
  return <div className="flex justify-center py-1.5 md:py-2" data-id="element-914" data-mp="2m0e66pvb11y">
      <ArrowDown className="h-5 w-5 text-seaglass-light/70" aria-hidden="true" data-id="element-915" data-mp="2kfp2wmboabh" />
    </div>;
}
function Chips({
  items
}: {
  items: string[];
}) {
  return <div className="mt-2.5 flex flex-wrap gap-2 md:mt-0" data-id="element-916" data-mp="3otqmsplvamfe">
      {items.map(item => <span key={item} className="rounded-lg border border-white/12 bg-navy-light/40 px-3 py-1.5 text-sm text-paper/85" data-id="element-917" data-mp="3ordxtshozbsu">
          {item}
        </span>)}
    </div>;
}
function Stage({
  n,
  title,
  children
}: {
  n: string;
  title: string;
  children: React.ReactNode;
}) {
  return <div className="rounded-2xl border border-white/12 bg-white/[0.05] px-4 py-4 md:grid md:grid-cols-[15rem_1fr] md:items-center md:gap-5 md:px-5 md:py-4" data-id="element-918" data-mp="3ktpvw5hl0irg">
      <div className="flex items-center gap-3" data-id="element-919" data-mp="1ljna91he595g">
        <span className="text-sm font-semibold text-seaglass-light/70" data-id="element-920" data-mp="1livf1rrphdfp">{n}</span>
        <p className="font-serif text-xl text-paper md:text-2xl" data-id="element-921" data-mp="1glv6993h1wkw">{title}</p>
      </div>
      <div className="mt-2.5 md:mt-0" data-id="element-922" data-mp="1cll281z8pg8o">{children}</div>
    </div>;
}
export function RevenueArchitecture() {
  const {
    openDiagnostic
  } = useScore();
  return <section className="bg-navy px-6 pb-14 pt-8 text-paper md:px-10 md:pb-16 md:pt-10" data-id="element-923" data-mp="24ao81m6ryynf">
      <div className="mx-auto max-w-5xl" data-id="element-924" data-mp="209cex6oozvah">
        <div className="max-w-3xl" data-id="element-925" data-mp="2089nmxngjqu1">
          <Reveal data-id="element-926" data-mp="20bfeahbqj0rv">
            <h2 className="font-serif text-3xl leading-[1.08] text-paper md:text-5xl" data-id="element-927" data-mp="1varo1xvbynyp">
              A better reader path creates
              <br data-id="element-928" data-mp="ovgxfrabfag3" />
              <span className="italic text-seaglass-light" data-id="element-929" data-mp="ow0hpenfuaxa">
                better business opportunities.
              </span>
            </h2>
            <p className="mt-3.5 text-lg leading-relaxed text-paper/70" data-id="element-930" data-mp="2viy4ey2lkx17">
              <span className="block" data-id="element-931" data-mp="2vglg8368xqfp">Readers get help using the book.</span>
              <span className="mt-1 block" data-id="element-932" data-mp="30iaoxc5zxs0m">
                You gain the context, assets, and pathways to connect the right
                people to the right work.
              </span>
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1} data-id="element-933" data-mp="3e963n536f3c4">
          <div className="mt-6 md:mt-7" data-id="element-934" data-mp="3e9z1fgk5qii4">
            <Stage n="1" title="The book" data-id="element-935" data-mp="3e8xrjfevo21z">
              <p className="text-base leading-relaxed text-paper/70 md:text-lg" data-id="element-936" data-mp="3j80nmacvqxxt">
                Ideas, frameworks, stories, language, and offers
              </p>
            </Stage>
            <Down data-id="element-937" data-mp="3s8ypalzuitd8" />
            <Stage n="2" title="A better reader path" data-id="element-938" data-mp="3s9gu470kqfwr">
              <Chips items={readerPath} data-id="element-939" data-mp="3s6uoicx8uah6" />
            </Stage>
            <Down data-id="element-940" data-mp="1tudxl9qr9owt" />
            <Stage n="3" title="Known context + intent" data-id="element-941" data-mp="1tux4lvevasw2">
              <Chips items={knownContext} data-id="element-942" data-mp="1xue8ikenbhz4" />
            </Stage>
            <Down data-id="element-943" data-mp="1xx0ieqp0e9ki" />
            <Stage n="4" title="Fitting business paths" data-id="element-944" data-mp="12krb23tyooef">
              <div data-id="element-945" data-mp="12j6de5ccopcc">
                <div className="flex flex-wrap gap-2" data-id="element-946" data-mp="12mu4kxuboznw">
                  {businessPaths.map(({
                  label,
                  Icon
                }) => <span key={label} className="inline-flex items-center gap-2 rounded-lg border border-seaglass/30 bg-white/[0.06] px-3 py-1.5 text-sm text-paper" data-id="element-947" data-mp="1cj6x3se4t7h2">
                      <Icon className="h-4 w-4 flex-shrink-0 text-seaglass-light" aria-hidden="true" data-id="element-948" data-mp="ju9ka4sow2vp" />
                      {label}
                    </span>)}
                </div>
                <p className="mt-3 text-sm leading-relaxed text-paper/55" data-id="element-949" data-mp="et3qe3nam9kl">
                  And other offer paths built around the author’s work.
                </p>
              </div>
            </Stage>
            <Down data-id="element-950" data-mp="orjgc3al3csi" />
            <div className="rounded-3xl border-2 border-sand bg-sand/15 px-5 py-6 text-center md:px-6 md:py-7" data-id="element-951" data-mp="13iefd7wuum2">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sand/80" data-id="element-952" data-mp="3sal0xrct0cpy">
                The destination
              </p>
              <p className="mt-2 font-serif text-3xl font-semibold text-sand md:text-5xl" data-id="element-953" data-mp="27qpqci0ajjum">
                Better-fit business opportunity
              </p>
            </div>

            <div className="mt-3 rounded-2xl border border-white/12 border-t-2 border-t-sand/40 bg-white/[0.03] p-4 md:p-5" data-id="element-954" data-mp="1ysdqrqhg5bq1">
              <p className="text-base font-medium leading-relaxed text-paper/80 md:text-lg" data-id="element-955" data-mp="22t7digj5kkhc">
                The same build helps the author create and support those
                opportunities.
              </p>
              <div className="mt-3.5 grid gap-5 md:grid-cols-3 md:gap-6" data-id="element-956" data-mp="2vf8z0yn95yn8">
                {enablementGroups.map(group => <div key={group.label} data-id="element-957" data-mp="2hk6htfcygmv4">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-sand md:text-[0.8125rem]" data-id="element-958" data-mp="2mfvn7zbnoxzc">
                      {group.label}
                    </p>
                    <div className="mt-2.5 flex flex-wrap gap-2" data-id="element-959" data-mp="1b1i0ixsn2okw">
                      {group.items.map(item => <span key={item} className="rounded-lg border border-sand/25 bg-sand/10 px-3 py-1.5 text-[0.9375rem] leading-snug text-paper/85" data-id="element-960" data-mp="3cqnzlze6saqf">
                          {item}
                        </span>)}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} data-id="element-961" data-mp="2yyhleiulgdtm">
          <div className="mx-auto mt-6 max-w-3xl text-center md:mt-7" data-id="element-962" data-mp="2yzakcxnb9z17">
            <p className="mx-auto max-w-3xl font-serif text-2xl leading-snug text-paper md:text-3xl" data-id="element-963" data-mp="2u18zc76brpn3">
              <span className="block lg:whitespace-nowrap" data-id="element-964" data-mp="2p0lb1t9hdmwk">
                Author Leverage helps the author get more business value
              </span>
              <span className="block lg:whitespace-nowrap" data-id="element-965" data-mp="2g0y6wzpda2zt">
                from the book by making it more useful to readers
              </span>
              <span className="block lg:whitespace-nowrap" data-id="element-966" data-mp="i9lq6n0avko0">
                and more usable across the business.
              </span>
            </p>
            <div className="mt-5 flex justify-center md:mt-6" data-id="element-967" data-mp="1w6ybpandva5w">
              <Cta variant="primary" showArrow onClick={openDiagnostic} className="px-10 py-4 text-lg" data-id="element-968" data-mp="1w6ohfavb2gue">
                Get Your Reader-to-Revenue Score
              </Cta>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}